
import React from "react";
import { Star, FileText, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { Testimonial } from "./types";

interface TestimonialCardProps {
  testimonial: Testimonial;
  itemVariants: any;
  initialPosition: { x: number; y: number };
  index: number;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ 
  testimonial, 
  itemVariants, 
  initialPosition,
  index
}) => {
  return (
    <motion.div 
      variants={itemVariants}
      className="w-[280px] cursor-grab active:cursor-grabbing group"
      initial={{ opacity: 0, scale: 0.8, rotate: Math.random() * 6 - 3 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      drag
      dragMomentum={false}
      whileDrag={{ 
        scale: 1.05, 
        zIndex: 50,
        boxShadow: "0 10px 25px rgba(0,0,0,0.3)" 
      }}
      whileHover={{ scale: 1.02 }}
    >
      {/* Case file card with thumbtack */}
      <div className="absolute -top-3 left-1/2 w-2 h-2 bg-anzir rounded-full z-10 transform -translate-x-1/2"></div>
      
      {/* Case file card */}
      <div className="bg-black/90 border border-white/10 shadow-md overflow-hidden transition-all duration-300 group-hover:shadow-xl p-0 drag-me-cursor rounded-lg">
        {/* Case file header */}
        <div className="bg-black/80 p-3 border-b border-white/20">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <FileText className="h-4 w-4 text-anzir" />
              <h4 className="font-medium text-white text-sm">WITNESS #{testimonial.id}</h4>
            </div>
            <div className="flex">
              {Array.from({ length: testimonial.rating }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-anzir text-anzir" />
              ))}
            </div>
          </div>
        </div>
        
        {/* Testimony content */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex mb-3">
            <div className="mr-3 flex-shrink-0">
              <div className="w-14 h-14 rounded-lg overflow-hidden border border-white/20">
                <img 
                  src={testimonial.image || testimonial.avatar} 
                  alt={testimonial.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="text-white font-medium">{testimonial.name}</div>
              <div className="text-xs text-gray-400 mb-1">{testimonial.role}</div>
              <div className="flex items-center text-xs text-gray-400">
                <MapPin className="w-3 h-3 mr-1" />
                <span>{testimonial.location}</span>
              </div>
            </div>
          </div>
          
          {/* Testimony quote with dark glass morphism style */}
          <div className="glass-morphism p-3 mb-3 rounded">
            <p className="text-white/90 text-sm leading-relaxed">"
              {testimonial.quote || testimonial.content}"
            </p>
          </div>
          
          {/* Evidence tag */}
          <div className="flex justify-end mb-2">
            <div className="inline-block bg-black/60 border border-anzir/50 px-2 py-0.5 text-xs text-anzir rounded">
              Credibility: {testimonial.rating}/5
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TestimonialCard;
